import { Button } from "PFComponents/button";
import { SelectV2 } from "PFComponents/select_v2";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { FeedbackRoundInfo } from "PFTypes/activity_participant";
import { Option } from "PFTypes/options";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFeedbackContext } from "./feedback_context";
import css from "./form/feedback_form.module.scss";
import { useSavedFeedbackForms } from "./hooks/use_saved_feedback_forms";
import { ManagerFeedbackForm } from "./manager_feedback_form";
import { NoFeedbackPlaceholder } from "./no_feedback_placeholder";

export const FormSection = () => {
  const { setActiveFeedbackRound, activeFeedbackRound, selectedParticipant, selectedRole, selectedEntry } =
    useFeedbackContext();
  const { resetSavedForm } = useSavedFeedbackForms();
  const { t } = useTranslation("activities", { keyPrefix: "show.feedback" });
  const { t: commonT } = useTranslation();
  const [isCreatingNewFeedback, setIsCreatingNewFeedback] = useState(false);
  const { formatDateTime } = useDateFormatter();

  useEffect(() => {
    setIsCreatingNewFeedback(false);
  }, [selectedEntry]);

  const selectHandler = ({ original: selectedRound }: Option<FeedbackRoundInfo>) => {
    if (selectedRound.id === activeFeedbackRound?.id) {
      return;
    }
    setActiveFeedbackRound(selectedRound);
    resetSavedForm();
  };

  const fullName = getProfileName(selectedParticipant);
  const isFeedbackListEmpty = selectedRole?.feedback?.length === 0;

  const feedbackRoundOptions: Option<FeedbackRoundInfo>[] = (selectedRole?.feedback || []).map(
    (feedbackInfo) => ({
      id: feedbackInfo.id,
      value: formatDateTime(feedbackInfo.submittedAt) || String(feedbackInfo.round),
      original: feedbackInfo
    })
  );

  if (isFeedbackListEmpty && !isCreatingNewFeedback) {
    return <NoFeedbackPlaceholder onClick={() => setIsCreatingNewFeedback(true)} />;
  }

  return (
    <>
      <div className={css.formHeader}>
        <h2>{fullName}</h2>
        <div className={css.headerRightSection}>
          {isCreatingNewFeedback ? (
            <>
              <span>{t("addingNewFeedback")}</span>
              <Button
                text={commonT("cancel")}
                kind="secondary"
                onClick={() => setIsCreatingNewFeedback(false)}
              />
            </>
          ) : (
            <>
              <SelectV2<FeedbackRoundInfo>
                value={activeFeedbackRound?.id}
                options={feedbackRoundOptions}
                onChange={selectHandler}
                className={css.roundSelect}
              />
              <Button
                className={css.addFeedbackButton}
                text={t("addFeedback")}
                onClick={() => setIsCreatingNewFeedback(true)}
              />
            </>
          )}
        </div>
      </div>
      {selectedParticipant && selectedRole && (
        <ManagerFeedbackForm
          activeFeedbackRound={isCreatingNewFeedback ? null : activeFeedbackRound}
          selectedParticipant={selectedParticipant}
          selectedRole={selectedRole}
          onSubmit={() => isCreatingNewFeedback && setIsCreatingNewFeedback(false)}
        />
      )}
    </>
  );
};
